import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { useToast } from '@/hooks/use-toast';
import { services, subscriptionRepository } from '@/lib/services';
import { rememberUpgradeReturn } from '@/lib/upgrade-return';
import { useLocation } from 'wouter';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (tier: string) => void;
  requiredTier: 'Nobility' | 'Royalty' | 'Imperial';
  featureName: string;
  userTier: string;
  userRoles: string[];
  rawUserData: {
    subscriptionTier?: string;
    roles?: any;
    email?: string;
  };
}

type TierName = 'Nobility' | 'Royalty' | 'Imperial';
type TierSlug = 'nobility' | 'royalty' | 'imperial';

interface UpgradePlanData {
  tier: TierName;
  slug: TierSlug;
  priceValue: number;
  priceLabel: string;
  priceId: string;
  color: string;
  features: string[];
}

const TIER_SLUG_MAP: Record<TierName, TierSlug> = {
  Nobility: 'nobility',
  Royalty: 'royalty',
  Imperial: 'imperial',
};

const TIER_ORDER: Record<'free' | TierSlug, number> = {
  free: 0,
  nobility: 1,
  royalty: 2,
  imperial: 3,
};

const PAID_TIERS: TierName[] = ['Nobility', 'Royalty', 'Imperial'];

export function UpgradeModal({
  isOpen,
  onClose,
  onUpgrade,
  requiredTier,
  featureName,
  userTier,
  userRoles,
  rawUserData
}: UpgradeModalProps) {
  const { user } = useFirebaseAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isRefreshingPlans, setIsRefreshingPlans] = useState(false);
  const [hasLoadedLivePlans, setHasLoadedLivePlans] = useState(false);
  const [checkoutPlanSlug, setCheckoutPlanSlug] = useState<TierSlug | null>(null);
  const [plans, setPlans] = useState<UpgradePlanData[]>([]);

  // Keep these props intentionally consumed for compatibility/debug context.
  void userRoles;
  void rawUserData;

  const normalizeTierSlug = (value?: string): 'free' | TierSlug => {
    const normalized = (value || '').toLowerCase();
    if (normalized.includes('imperial')) return 'imperial';
    if (normalized.includes('royalty') || normalized.includes('royal')) return 'royalty';
    if (normalized.includes('nobility')) return 'nobility';
    return 'free';
  };

  const parsePrice = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const match = value.match(/\d+(\.\d+)?/);
      const parsed = match ? Number(match[0]) : Number.NaN;
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

  const getTierInfo = (tier: TierName): Omit<UpgradePlanData, 'tier' | 'slug' | 'priceLabel'> => {
    switch (tier) {
      case 'Nobility':
        return {
          priceValue: 19.99,
          priceId: 'priceNobility6months',
          color: 'text-emerald-300 border-emerald-500/20 bg-emerald-500/10',
          features: [
            'Create professional profile',
            'Get discovered by students',
            'Receive connect requests',
            'Sell curriculum resources',
          ],
        };
      case 'Royalty':
        return {
          priceValue: 34.99,
          priceId: 'priceRoyalty6months',
          color: 'text-sky-300 border-sky-500/20 bg-sky-500/10',
          features: [
            'Everything in Nobility',
            'Create jobs and contact professionals',
            'Book dance professionals',
            'Priority support',
          ],
        };
      case 'Imperial':
        return {
          priceValue: 49.99,
          priceId: 'priceImperial6months',
          color: 'text-violet-300 border-violet-500/20 bg-violet-500/10',
          features: [
            'Everything in Royalty',
            'Premium seller visibility',
            'Advanced growth tools',
            'Highest priority support',
          ],
        };
    }
  };

  const buildFallbackPlans = (): UpgradePlanData[] => {
    return PAID_TIERS.map((tier) => {
      const tierInfo = getTierInfo(tier);
      return {
        tier,
        slug: TIER_SLUG_MAP[tier],
        ...tierInfo,
        priceLabel: formatPrice(tierInfo.priceValue),
      };
    });
  };

  const resolvePlanPrice = (planDoc: Record<string, unknown>, fallback: number): number => {
    const candidates: unknown[] = [
      planDoc.price,
      planDoc.priceMonthly,
      planDoc.priceMonthly,
      planDoc.price6Months,
      planDoc.price6Months,
      planDoc.priceYearly,
    ];
    for (const candidate of candidates) {
      const parsed = parsePrice(candidate);
      if (parsed !== null) return parsed;
    }
    return fallback;
  };

  const resolvePriceId = (planDoc: Record<string, unknown>, fallback: string): string => {
    const candidates: unknown[] = [
      planDoc.stripePriceId,
      planDoc.stripePriceId6Months,
      planDoc.stripePriceIdMonthly,
      planDoc.stripePriceIdYearly,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate;
      }
    }
    return fallback;
  };

  const resolvePlanFeatures = (
    planDoc: Record<string, unknown> | undefined,
    fallback: string[]
  ): string[] => {
    if (!planDoc) return fallback;
    const rawFeatures = planDoc.features;
    if (!Array.isArray(rawFeatures)) return fallback;
    const normalized = rawFeatures.filter(
      (value): value is string => typeof value === 'string' && value.trim().length > 0
    );
    return normalized.length > 0 ? normalized : fallback;
  };

  // Fetch all paid plan data when modal opens
  useEffect(() => {
    if (!isOpen || hasLoadedLivePlans) return;
    void fetchPlansData();
  }, [isOpen, hasLoadedLivePlans]);

  const fetchPlansData = async () => {
    try {
      setIsRefreshingPlans(true);
      const planDocs = await subscriptionRepository.getSubscriptionPlans();

      const docsBySlug = new Map<TierSlug, Record<string, unknown>>();
      planDocs.forEach((plan) => {
        const data = plan as Record<string, unknown>;
        const slugCandidate = normalizeTierSlug(
          String(data.slug || data.tier || data.name || '')
        );
        if (slugCandidate !== 'free') {
          docsBySlug.set(slugCandidate, data);
        }
      });

      const resolvedPlans = PAID_TIERS.map((tier) => {
        const base = getTierInfo(tier);
        const slug = TIER_SLUG_MAP[tier];
        const dbPlan = docsBySlug.get(slug);
        const priceValue = dbPlan ? resolvePlanPrice(dbPlan, base.priceValue) : base.priceValue;
        const priceId = dbPlan ? resolvePriceId(dbPlan, base.priceId) : base.priceId;
        const features = resolvePlanFeatures(dbPlan, base.features);

        return {
          ...base,
          tier,
          slug,
          priceValue,
          priceId,
          features,
          priceLabel: formatPrice(priceValue),
        };
      });

      setPlans(resolvedPlans);
      setHasLoadedLivePlans(true);
    } catch (error) {
      console.error('Failed to fetch pricing table data:', error);
      if (plans.length === 0) {
        setPlans(buildFallbackPlans());
      }
      toast({
        title: 'Warning',
        description: 'Could not load live pricing. Showing default plan prices.',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshingPlans(false);
    }
  };

  const handleDirectCheckout = async (plan: UpgradePlanData) => {
    if (!plan || !user) {
      toast({
        title: 'Error',
        description: 'Missing plan data or user authentication.',
        variant: 'destructive',
      });
      return;
    }

    setCheckoutPlanSlug(plan.slug);
    try {
      const checkoutResult = await services.subscription.createSubscriptionFromPlan({
        userId: user.id,
        planId: plan.slug,
        frequency: '6months',
      });

      const checkoutUrl = checkoutResult.checkoutUrl;
      if (!checkoutUrl || typeof checkoutUrl !== 'string') {
        throw new Error(`Invalid checkout URL received: ${checkoutUrl}`);
      }

      // Come back here after paying, not to the dashboard. Read before onClose,
      // which may navigate away.
      rememberUpgradeReturn(`${window.location.pathname}${window.location.search}`);
      onClose();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to start checkout process. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCheckoutPlanSlug(null);
    }
  };

  const currentTierSlug = normalizeTierSlug(userTier);
  const requiredTierSlug = TIER_SLUG_MAP[requiredTier];
  const currentTierLevel = TIER_ORDER[currentTierSlug];
  const requiredTierLevel = TIER_ORDER[requiredTierSlug];

  const getCtaLabel = (
    plan: UpgradePlanData,
    isInsufficientForFeature: boolean
  ): string => {
    if (plan.slug === currentTierSlug) return 'Current Plan';
    if (isInsufficientForFeature) return 'Insufficient for this feature';
    if (TIER_ORDER[plan.slug] <= currentTierLevel) return 'Included in your plan';
    return `Upgrade to ${plan.tier}`;
  };

  const plansToRender = plans.length > 0 ? plans : buildFallbackPlans();

  // Not onClose: on the panel guards that means "go back", which used to
  // run first and leave the member on the previous page instead of pricing.
  const handleViewFullPricing = () => {
    const before = `${window.location.pathname}${window.location.search}`;
    onUpgrade(requiredTier);
    // Call sites that navigate themselves (the connect wizard) win.
    if (`${window.location.pathname}${window.location.search}` === before) {
      navigate(`/subscription?tier=${requiredTier}`);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        overlayClassName="bg-black"
        className="bg-slate-950 border-slate-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center">Upgrade Required</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-center">
            <p className="text-gray-300 text-sm">
              You're currently on the <span className="font-semibold text-blue-400">{userTier}</span> plan.
              Upgrade to unlock <span className="font-semibold text-green-400">{featureName}</span>.
            </p>
          </div>

          {isRefreshingPlans && (
            <div className="text-center text-[11px] text-gray-400 -mt-1">
              Updating pricing...
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-3">
            {plansToRender.map((plan) => {
                const isCurrentPlan = plan.slug === currentTierSlug;
                const isIncludedAtCurrentTier =
                  !isCurrentPlan && TIER_ORDER[plan.slug] <= currentTierLevel;
                const isInsufficientForFeature =
                  TIER_ORDER[plan.slug] < requiredTierLevel;
                const isRecommended = plan.slug === requiredTierSlug;
                const isProcessing = checkoutPlanSlug === plan.slug;
                const isDisabled =
                  isCurrentPlan ||
                  isIncludedAtCurrentTier ||
                  isInsufficientForFeature ||
                  checkoutPlanSlug !== null;

                return (
                  <div
                    key={plan.slug}
                    className={`rounded-lg border p-4 bg-slate-900 flex h-full flex-col ${
                      isRecommended
                        ? 'border-cyan-400/60 ring-1 ring-cyan-400/40'
                        : isInsufficientForFeature
                        ? 'border-amber-500/40'
                        : 'border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Badge className={`${plan.color} px-3 py-1`}>
                        <span className="font-semibold">{plan.tier}</span>
                      </Badge>
                      {isRecommended && (
                        <span className="text-[10px] px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                          Required for this feature
                        </span>
                      )}
                    </div>

                    {isInsufficientForFeature && (
                      <div className="mb-3 text-[11px] px-2 py-1 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/30 text-center">
                        Insufficient for {featureName}
                      </div>
                    )}

                    <div className="text-center mb-3">
                      <div className="text-2xl font-bold text-white">{plan.priceLabel}</div>
                      <div className="text-xs text-gray-400">every 6 months</div>
                    </div>

                    <ul className="space-y-2 text-sm mb-4 min-h-[132px] flex-1">
                      {[...plan.features].reverse().map((planFeature, index) => (
                        <li key={index} className="flex items-start text-gray-300">
                          <CheckCircle2 className="h-4 w-4 text-green-400 mr-2 mt-0.5 shrink-0" />
                          <span>{planFeature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleDirectCheckout(plan)}
                      disabled={isDisabled}
                      className="w-full font-semibold disabled:opacity-60"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        getCtaLabel(plan, isInsufficientForFeature)
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Maybe Later
            </Button>
            <Button
              variant="outline"
              onClick={handleViewFullPricing}
              className="flex-1 border-cyan-500 text-cyan-300 hover:bg-cyan-500/10"
            >
              View Full Pricing
            </Button>
          </div>

          <div className="text-center text-xs text-gray-500">
            Unlock {featureName.toLowerCase()} and grow your dance business
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
